// The cache backend over the signed-in account's IndexedDB (api/db.ts, its
// `kv` store). Where there is no database (IndexedDB unavailable, or nobody
// signed in), it keeps entries in this page's memory instead and says its
// writes are not durable.
//
// A backend belongs to the account signed in when it was made: once
// another signs in, it reads nothing and refuses writes (`unavailable`), so
// a write begun for one account never lands in the next one's database.
import { db } from '../api/db';
import { identityGeneration } from '../api/identity';
import { memoryBackend, storageFailureOf, type Backend, type WriteResult } from './cache';

const memory = memoryBackend();

const SWITCHED: WriteResult = Object.freeze({ ok: false, failure: { kind: 'unavailable' as const, message: 'another account signed in' } });

/** The `kv` store of the signed-in account's database, else this page's memory; for that account only. */
export function idbBackend(): Backend {
  const generation = identityGeneration();
  const mine = () => identityGeneration() === generation;
  return {
    async get(key) {
      const d = await db();
      if (!mine()) return undefined;
      if (!d) return memory.get(key);
      try {
        return await d.get('kv', key);
      } catch {
        return undefined;
      }
    },
    async scan(prefix) {
      const d = await db();
      if (!mine()) return [];
      if (!d) return memory.scan(prefix);
      try {
        const out: [string, unknown][] = [];
        const tx = d.transaction('kv');
        for (let c = await tx.store.openCursor(IDBKeyRange.bound(prefix, prefix + '￿')); c; c = await c.continue())
          out.push([String(c.key), c.value]);
        return out;
      } catch {
        return [];
      }
    },
    async write(entries) {
      const d = await db();
      if (!mine()) return SWITCHED;
      if (!d) return memory.write(entries);
      if (!entries.length) return { ok: true, durable: true };
      try {
        const tx = d.transaction('kv', 'readwrite');
        // `tx.done` joins the writes, so an aborted transaction's rejection is always handled.
        await Promise.all([...entries.map(([k, v]) => (v === undefined ? tx.store.delete(k) : tx.store.put(v, k))), tx.done]);
        return { ok: true, durable: true };
      } catch (e) {
        return { ok: false, failure: storageFailureOf(e) };
      }
    },
  };
}
